import { Permission } from '@today/types'
import { AuthConfigForm, ProviderForm, ProviderList, RotateCertificateForm } from '@today/ui-domain'
import { useNavigate, useParams } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useTenantAuthentication } from '@/hooks/useTenantAuthentication'

type Auth = ReturnType<typeof useTenantAuthentication>

/** The open provider form: create, edit, or certificate rotation. */
function ProviderEditorPanel({ auth }: { auth: Auth }) {
	const { editor } = auth
	if (!editor) return null
	if (editor.mode === 'rotate') {
		return (
			<RotateCertificateForm
				key={`rotate-${editor.provider.id}`}
				providerName={editor.provider.name}
				isBusy={auth.isBusy}
				error={auth.editorError}
				onSubmit={auth.rotate}
				onCancel={auth.closeEditor}
			/>
		)
	}
	const provider = editor.mode === 'edit' ? editor.provider : null
	return (
		<ProviderForm
			key={provider?.id ?? 'create'}
			provider={provider}
			isBusy={auth.isBusy}
			error={auth.editorError}
			onSubmit={auth.submitProvider}
			onCancel={auth.closeEditor}
		/>
	)
}

/** Refusals and test outcomes of the list's own actions (enable, test login). */
function ListNotices({ auth }: { auth: Auth }) {
	return (
		<>
			{auth.listError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{auth.listError}
				</p>
			) : null}
			{auth.testMessage ? (
				<p role="status" className="text-ds-sm text-ds-text">
					{auth.testMessage}
				</p>
			) : null}
		</>
	)
}

/** The sign-in settings form; read-only without `tenant.manage_idp` (TO-593 REQ-008). */
function SignInSettings({ auth, isReadOnly }: { auth: Auth; isReadOnly: boolean }) {
	if (!auth.data) return null
	return (
		// Keyed on the stored config: a save re-seeds the inputs from the server's normalized values.
		<AuthConfigForm
			key={auth.data.config.updatedAt ?? 'defaults'}
			config={auth.data.config}
			defaultProviderOptions={auth.usableProviders.map(({ id, name }) => ({ id, name }))}
			isBusy={auth.isBusy}
			error={auth.configError}
			isReadOnly={isReadOnly}
			onSave={auth.saveConfig}
		/>
	)
}

/**
 * A tenant's Identity & login settings (TO-410 design D12): the sign-in
 * config and the identity providers, with a per-provider test login. Mounted
 * on both planes: `/t/:slug/admin/sso` for the Tenant Admin and
 * `/admin/tenants/:slug/sso` for the Super-Admin. Both call the same API.
 */
export function TenantSsoPage() {
	const { slug = '' } = useParams()
	const auth = useTenantAuthentication(slug)
	const navigate = useNavigate()
	// TO-377 REQ-201 / TO-593 REQ-008: "Attribute mapping" and editable sign-in settings need tenant.manage_idp.
	const canManageIdp = useViewerPermissions().permissions.includes(Permission.TENANT_MANAGE_IDP)

	if (auth.loadError) {
		return (
			<p role="alert" className="p-6 text-sm text-ds-danger">
				Could not load the identity & login settings: {auth.loadError}
			</p>
		)
	}
	if (!auth.data) {
		return (
			<p className="p-6 text-sm text-ds-text-muted" aria-live="polite">
				Loading identity & login settings…
			</p>
		)
	}
	return (
		<div className="flex max-w-3xl flex-col gap-8 p-6">
			<h2 className="text-ds-lg font-semibold text-ds-text">Identity & login · {slug}</h2>
			<SignInSettings auth={auth} isReadOnly={!canManageIdp} />
			<hr className="border-ds-border" />
			<ListNotices auth={auth} />
			<ProviderList
				providers={auth.data.providers}
				serviceProvider={auth.data.serviceProvider}
				isBusy={auth.isBusy}
				onAdd={() => auth.openEditor({ mode: 'create' })}
				onEdit={(provider) => auth.openEditor({ mode: 'edit', provider })}
				onRotate={(provider) => auth.openEditor({ mode: 'rotate', provider })}
				onToggleEnabled={auth.toggleEnabled}
				onTest={auth.test}
				onOpenMapping={
					canManageIdp ? (provider) => void navigate(`providers/${provider.id}/attribute-mapping`) : undefined
				}
			/>
			<ProviderEditorPanel auth={auth} />
		</div>
	)
}
